import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {FormBuilder} from "@angular/forms";
import {merge, Subscription} from "rxjs";
import { SuggestedWordService } from "./services/suggested-word.service";
import {Pending} from "../shared/decorators/pending.decorator";
import {DisposeMixin} from "../shared/mixins/dispose.mixin";
import {LockMixin} from "../shared/mixins/lock.mixin";
import {EmptyMixin} from "../shared/mixins/empty.mixin";
import {AggregationGroupModel} from "../shared/models/search/aggregations/aggregation-group.model";
import {SearchResultsFilterModel} from "../shared/models/search/results/filters/search-results-filter.model";
import {SearchResultItemModel} from "../shared/models/search/results/items/search-result-item.model";
import {WindowRefService} from "../shared/services/window-ref.service";
import {ContentSearchForm} from "./forms/content-search.form";
import {ContentSearchParamsModel} from "./models/search-params/content-search-params.model";
import {ContentSearchConfigService} from "./services/content-search-config.service";
import {ContentSearchService} from "./services/content-search.service";
import {ContentSearchParamsFactoryService} from "./services/factories/content-search-params-factory.service";
import {SearchTabProxyComponent} from "./components/form-controls/search-tab-proxy/search-tab-proxy.component";
import {ScopedSearchResultModel} from "./models/search-results/scoped-search-result.model";
import {ProductsParamsFilter} from "./models/search-params/filters/products-params-filter";
import {ProductSortingEnum} from "./components/tabs/products-tab/enums/productSortingEnum";

@Component({
    selector: "pcg-ng-content-search",
    template: require('./content-search.component.html')
})
export class ContentSearchComponent extends DisposeMixin(LockMixin(EmptyMixin)) implements OnInit, OnDestroy {
    private formSubscription: Subscription;

    @Pending
    private searchSubscription: Subscription;
    private suggestedWordSearchSubscription: Subscription;

    private previousText: string;
    private firstForcedSorting: boolean;

    public searchScopes: AggregationGroupModel;
    public searchForm: ContentSearchForm;
    public searchResult: ScopedSearchResultModel<SearchResultItemModel, SearchResultsFilterModel>;

    @ViewChild(SearchTabProxyComponent)
    public searchTabProxy: SearchTabProxyComponent;

    public constructor(
        private domElement: ElementRef,
        private formBuilder: FormBuilder,
        private configService: ContentSearchConfigService,
        private searchParamsFactory: ContentSearchParamsFactoryService,
        private contentSearchService: ContentSearchService,
        private suggestedWordService: SuggestedWordService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.configService.initialize(this.domElement.nativeElement.dataset);

        const searchParams = this.searchParamsFactory.createParamsByQueryString(WindowRefService.getQueryString());

        this.firstForcedSorting = searchParams.forcedSorting;
        this.previousText = searchParams.searchText;

        this.searchForm = ContentSearchForm.createForm(this.formBuilder, searchParams, this.searchParamsFactory, this.configService);
        this.formSubscription = merge (
            this.searchForm.changeObservable,
            this.searchForm.submitObservable
        )
        .subscribe((params: ContentSearchParamsModel) => this.runSearch(params));

        this.searchForm.submit();

        this.suggestedWordSearchSubscription = this.suggestedWordService.SuggestedWordObservable
            .subscribe(word => this.searchBySuggestedWord(word));
    }

    public ngOnDestroy(): void {
        this.dispose();
    }

    private searchBySuggestedWord(word: string): void {
        this.searchForm.searchText.setValue(word);
    }

    private runSearch(searchParams: ContentSearchParamsModel): void {
        const productFilter = searchParams.filter as ProductsParamsFilter;

        if (productFilter && searchParams.searchText !== this.previousText && !this.firstForcedSorting ) {
            if (searchParams.searchText) {
                productFilter.sorting = ProductSortingEnum.BestMatch;
            } else {
                productFilter.sorting = ProductSortingEnum.DateCreated;
            }
        }

        if (this.firstForcedSorting) {
            this.firstForcedSorting = false;
        }

        this.searchSubscription = this.contentSearchService.getSearchResult(searchParams)
            .subscribe(
                (result: ScopedSearchResultModel<SearchResultItemModel, SearchResultsFilterModel>) => {
                    this.searchResult = result;
                    this.searchScopes = result.searchScopes;

                    this.searchTabProxy.scope = searchParams.searchScope;
                    this.searchTabProxy.searchResult = result;
                    this.searchTabProxy.createTab();

                    this.previousText = searchParams.searchText;
                },
                () => {}
            );
    }
}
